import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getCsvData } from "../services/spatialDatasetService";

export interface NormalisedCsvData {
  columns: string[];
  rows: Record<string, unknown>[];
  /**
   * True when more rows exist beyond the current page, false when this is the last
   * page, null when the endpoint gives no signal (caller falls back to page-fill).
   */
  hasMore: boolean | null;
}

/**
 * Normalises the `/api/v2/csv-data/` response. The confirmed shape is columnar:
 *   { file_key, columns: string[], rows: [ {col: val}, ... ], count, offset, limit, truncated }
 * where `count` is the number of rows in THIS page (NOT the total) and `truncated`
 * is the authoritative "has more pages" flag. We also defensively handle a few other
 * shapes (DRF `{results,next}`, bare array, `{data}`) in case the contract changes.
 * Rows given as positional arrays are keyed back to column names.
 */
export function normaliseCsvData(raw: unknown, preferredColumns?: string[]): NormalisedCsvData {
  if (!raw || typeof raw !== "object") {
    return { columns: preferredColumns ?? [], rows: [], hasMore: false };
  }

  let rowsRaw: unknown[] = [];
  let columns: string[] = preferredColumns ? [...preferredColumns] : [];
  let hasMore: boolean | null = null;

  if (Array.isArray(raw)) {
    rowsRaw = raw;
  } else {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.results)) {
      rowsRaw = obj.results;
    } else if (Array.isArray(obj.rows)) {
      rowsRaw = obj.rows;
    } else if (Array.isArray(obj.data)) {
      rowsRaw = obj.data;
    }
    if (Array.isArray(obj.columns)) {
      columns = (obj.columns as unknown[]).map(String);
    }
    // `truncated` is the primary paging signal; DRF `next` is the fallback.
    if (typeof obj.truncated === "boolean") hasMore = obj.truncated;
    else if ("next" in obj) hasMore = obj.next != null;
  }

  // Derive columns from the first object row when no hint/columns were supplied.
  const firstRow = rowsRaw[0];
  if (columns.length === 0 && firstRow && typeof firstRow === "object" && !Array.isArray(firstRow)) {
    columns = Object.keys(firstRow as Record<string, unknown>);
  }

  const rows: Record<string, unknown>[] = rowsRaw.map((r) => {
    if (Array.isArray(r)) {
      const mapped: Record<string, unknown> = {};
      columns.forEach((c, i) => {
        mapped[c] = r[i];
      });
      return mapped;
    }
    return (r ?? {}) as Record<string, unknown>;
  });

  return { columns, rows, hasMore };
}

/**
 * Paginated, read-only CSV rows for a tabular SpatialDataset.
 *
 * @param endpoint    config.tabularData.csvData (e.g. "/api/v2/csv-data/")
 * @param fileKey     dataset.original_file_key (the raw CSV S3 key)
 * @param page        0-based page index
 * @param pageSize    rows per page (limit)
 * @param columnsHint ordered column names from stac_properties.column_schema
 * @param enabled     gate the query (e.g. dialog open)
 */
export const useCsvData = (
  endpoint: string,
  fileKey: string | null | undefined,
  page: number,
  pageSize: number,
  columnsHint?: string[],
  enabled = true
) => {
  return useQuery<NormalisedCsvData>({
    queryKey: ["csv-data", fileKey, pageSize, page],
    enabled: enabled && !!fileKey,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const raw = await getCsvData(endpoint, {
        file_key: fileKey as string,
        limit: pageSize,
        offset: page * pageSize
      });
      return normaliseCsvData(raw, columnsHint);
    }
  });
};
